import { sb } from '../supabase';

// Client-side funnel logging for the factory-claim flow. Fire-and-forget: it must
// NEVER block or break the page — every call swallows its own errors. Events are
// written through the log_claim_event RPC (anonymous-friendly, no table grant).
//
// A per-browser anon_id (localStorage) stitches one person's stages together even
// before they have an account; a user_id in meta (when known) bridges the SAME
// account across devices/browsers — the email-confirmation link is often opened in
// a different browser than the one that signed up.

const ANON_KEY = 'maabar.anonId';

function uuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* older browsers fall through */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getAnonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) { id = uuid(); localStorage.setItem(ANON_KEY, id); }
    return id;
  } catch {
    return null;   // private mode: still log, just without a stable anon id
  }
}

// Detect known in-app browsers (WhatsApp / WeChat / Meta / Line) where OAuth and
// cross-app email-confirmation links commonly break — a prime claim drop-off cause
// worth flagging on every event so we can see if it correlates with stalls.
export function detectInApp() {
  try {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    if (/WhatsApp/i.test(ua)) return 'whatsapp';
    if (/MicroMessenger/i.test(ua)) return 'wechat';
    if (/FBAN|FBAV|Instagram/i.test(ua)) return 'meta';
    if (/\bLine\//i.test(ua)) return 'line';
    return null;
  } catch { return null; }
}

export function logClaimEvent(slug, stage, meta = {}) {
  try {
    const inapp = detectInApp();
    const payload = {
      p_slug: slug || null,
      p_stage: stage,
      p_anon_id: getAnonId(),
      p_meta: { ...(meta || {}), ...(inapp ? { inapp } : {}) },
      p_user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
      p_referrer: (typeof document !== 'undefined' && document.referrer) || null,
    };
    // Fire-and-forget: never await in a way that blocks the UI, never throw.
    sb.rpc('log_claim_event', payload).then(() => {}, () => {});
  } catch { /* logging must never break the flow */ }
}

// Capture technical failures (script errors + unhandled promise rejections) on the
// claim / callback pages, so a page that broke is distinguishable from a person who
// simply left. Returns a cleanup fn for useEffect. React render crashes are better
// caught by an ErrorBoundary; these listeners catch the common async/network kind.
export function installClaimErrorCapture(slug, tag) {
  const onErr = (e) => {
    const message = String(
      (e && (e.message || (e.reason && (e.reason.message || e.reason)))) || 'error'
    ).slice(0, 300);
    logClaimEvent(slug, 'page_error', {
      tag,
      message,
      source: e && e.filename ? String(e.filename).slice(0, 200) : undefined,
    });
  };
  try {
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onErr);
  } catch { /* SSR / no window */ }
  return () => {
    try {
      window.removeEventListener('error', onErr);
      window.removeEventListener('unhandledrejection', onErr);
    } catch { /* noop */ }
  };
}
